import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Box, ButtonLink, Card, Heading, IconLoader, Stack, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';

import type { TopTaskCTAContent } from './top-task-cta-content.types';

/**
 * `TopTaskCTA` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/TopTaskCTA/TopTaskCTA.tsx`): a
 * `Card` with a title and rich-text body on one side, and a button (its own icon plus visible label) on
 * the other — stacked on mobile, side by side from `md` up.
 *
 * The migrated Sanity schema models each `TopTasks` tile as its own standalone `TopTaskCTA` block rather
 * than an item nested inside `TopTasks` — see `top-tasks-content.types.ts` for how that was confirmed.
 *
 * Known simplification: the legacy component wraps itself in `TrackViewComponent` (from `@eneco/tracking`)
 * for view tracking — `lib:tracking` is not in `lib:cms-components`'s allowed dependencies (`nx.json`),
 * so that wrapper is dropped; the card itself renders unchanged.
 */
const TopTaskCTA: FC = () => {
  const { adapter } = useCms();
  const content = useContent<TopTaskCTAContent>();
  const isMobile = !useMediaQuery('md');

  const link = adapter.toLink(content.buttonLink);
  const html = adapter.toRichText(content.text);

  if (!link?.href) return null;

  return (
    <Card>
      <Box padding="6">
        <Stack direction={isMobile ? 'column' : 'row'} alignX="justify" alignY="center" gap={isMobile ? '4' : '6'}>
          <Stack.Item>
            {content.title && (
              <Box paddingBottom="2">
                <Heading as="h2" size="XS">
                  {content.title}
                </Heading>
              </Box>
            )}
            {html && (
              <Text>
                <RichTextComponent html={html} />
              </Text>
            )}
          </Stack.Item>

          <Stack.Item>
            <ButtonLink size="compact" href={link.href}>
              <IconLoader iconName={content.buttonIcon} fallbackIcon={null} />
              {link.text}
            </ButtonLink>
          </Stack.Item>
        </Stack>
      </Box>
    </Card>
  );
};

export default TopTaskCTA;
